import React, { useContext, useState } from 'react';
import { Box, Collapse, Divider, Slider, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from '@mui/material';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useTranslation } from 'react-i18next';
import AppContext from '../../context/AppContext';
import TravelContext, { AUTO_SEARCH_RADIUS } from '../../context/TravelContext';
import SecondaryMenuDrawer from '../../frame/components/other/SecondaryMenuDrawer';
import HeaderWithUnderline from '../../frame/components/header/HeaderWithUnderline';
import ActionIconBtn from '../../frame/components/btns/ActionIconBtn';
import { ReactComponent as ResetIcon } from '../../assets/icons/ic_action_reset_to_default_dark.svg';
import SelectItemWithoutOptions from '../../frame/components/items/SelectItemWithoutOptions';
import SelectItem from '../../frame/components/items/SelectItem';
import SimpleItemWithSwitch from '../../frame/components/items/SimpleItemWithSwitch';
import ThickDivider from '../../frame/components/dividers/ThickDivider';
import ColorBlock from '../../frame/components/other/ColorBlock';
import TagFilter from './TagFilter';
import { formatRadius, TAG_MATCH_MODES } from './TravelMenu';
import styles from './travel.module.css';
import {
    convertMeters,
    convertSpeedMS,
    getLargeLengthUnit,
    getSpeedUnit,
    LARGE_UNIT,
} from '../settings/units/UnitsConverter';

// metres, as the dropped-pin radius of Komoot, up to 500 km
const SEARCH_RADIUS_OPTIONS = [
    AUTO_SEARCH_RADIUS,
    100,
    250,
    500,
    1000,
    5000,
    10000,
    20000,
    30000,
    50000,
    100000,
    200000,
    500000,
];

export default function TravelFilters({
    onClose,
    onReset,
    hasActiveFilters,
    filters,
    bounds,
    setFilter,
    previewFilter,
}) {
    const ctx = useContext(AppContext);
    const ttx = useContext(TravelContext);
    const { t } = useTranslation();

    const [showTagFilters, setShowTagFilters] = useState(true);
    const [showAdvancedFilters, setShowAdvancedFilters] = useState(true);

    // One range slider driven by `filters[key]` (null when not applied → shows full bounds).
    const rangeSlider = ({ key, title, step, format, unit }) => {
        const [lo, hi] = bounds[key];
        const range = filters[key] ?? bounds[key];
        const active = filters[key] != null;
        const toFilter = (value) => (value[0] === lo && value[1] === hi ? null : value);

        return (
            <Box className={styles.sliderContainer} key={key}>
                <div className={styles.sliderHeader}>
                    <Typography className={styles.sliderTitle}>{title}</Typography>
                    <Typography className={styles.sliderValue}>
                        {format(range[0])} - {format(range[1])} {unit}
                    </Typography>
                </div>
                <Slider
                    value={range}
                    onChange={(e, value) => previewFilter(key, toFilter(value))}
                    onChangeCommitted={(e, value) => setFilter(key, toFilter(value))}
                    min={lo}
                    max={hi}
                    step={step}
                    disabled={lo === hi}
                    valueLabelDisplay="off"
                    sx={{
                        '& .MuiSlider-thumb': { opacity: active ? 1 : 0.5 },
                        '& .MuiSlider-track': { opacity: active ? 1 : 0.5 },
                    }}
                />
                <div className={styles.sliderBounds}>
                    <Typography className={styles.sliderBoundValue}>{format(lo)}</Typography>
                    <Typography className={styles.sliderBoundValue}>{format(hi)}</Typography>
                </div>
            </Box>
        );
    };

    const meters = (v) => convertMeters(v, ctx.unitsSettings.len, LARGE_UNIT).toFixed(0);
    const speed = (v) => convertSpeedMS(v / 3.6, ctx.unitsSettings.speed).toFixed(0);
    const plain = (v) => v;

    const rangeFilterGroups = [
        [
            { key: 'distance', title: t('distance'), step: 1000, format: meters, unit: t(getLargeLengthUnit(ctx)) },
            {
                key: 'maxDistBetweenPoints',
                title: t('web:max_distance_between_points'),
                step: 10,
                format: plain,
                unit: t('m'),
            },
        ],
        [
            { key: 'speed', title: t('shared_string_speed'), step: 1, format: speed, unit: t(getSpeedUnit(ctx)) },
            {
                key: 'maxSpeed',
                title: t('shared_string_max_speed'),
                step: 1,
                format: speed,
                unit: t(getSpeedUnit(ctx)),
            },
        ],
        [
            {
                key: 'timeMinutes',
                title: t('shared_string_time_span'),
                step: 1,
                format: plain,
                unit: t('shared_string_minute_lowercase'),
            },
            { key: 'waypoints', title: t('web:waypoints'), step: 1, format: plain, unit: '' },
        ],
    ];

    return (
        <SecondaryMenuDrawer onClose={onClose}>
            <HeaderWithUnderline
                title={t('shared_string_filters')}
                onClose={onClose}
                showBackButton
                titleId="se-travel-filters-title"
                rightContent={
                    <Tooltip title={t('reset_to_default')} arrow>
                        <span>
                            <ActionIconBtn
                                id="se-travel-filters-reset"
                                icon={<ResetIcon />}
                                onClick={onReset}
                                disabled={!hasActiveFilters}
                            />
                        </span>
                    </Tooltip>
                }
            />
            <Box className={styles.filtersBody}>
                <Box className={styles.filtersScroll}>
                    <SelectItem
                        title={t('web:travel_search_radius')}
                        value={ttx.travelSearchRadius}
                        options={radiusOptions(ttx.travelSearchRadius)}
                        getOptionLabel={(meters) =>
                            meters === AUTO_SEARCH_RADIUS
                                ? t('web:travel_search_radius_auto')
                                : formatRadius(meters, ctx, t)
                        }
                        getOptionValue={(meters) => meters}
                        onSelect={ttx.setTravelSearchRadius}
                        showDivider={false}
                    />
                    <ThickDivider mt={0} />
                    <SelectItemWithoutOptions
                        title={t('web:tag_filters')}
                        onClick={() => setShowTagFilters(!showTagFilters)}
                        endIcon={
                            showTagFilters ? (
                                <ExpandLessIcon sx={{ color: 'var(--text-secondary)' }} />
                            ) : (
                                <ExpandMoreIcon sx={{ color: 'var(--text-secondary)' }} />
                            )
                        }
                        showValue={false}
                    />
                    <Collapse in={showTagFilters}>
                        <TagFilter selectedTags={filters.tags} onChangeTags={(tags) => setFilter('tags', tags)} />
                        <Box className={styles.tagMatchBox}>
                            <ToggleButtonGroup
                                fullWidth
                                size="small"
                                className={styles.tagMatchToggleGroup}
                                exclusive
                                value={filters.tagMatchMode}
                                onChange={(event, value) => {
                                    if (value) {
                                        setFilter('tagMatchMode', value);
                                    }
                                }}
                            >
                                <ToggleButton value={TAG_MATCH_MODES.OR}>{t('web:matches_any_tag')}</ToggleButton>
                                <ToggleButton value={TAG_MATCH_MODES.AND}>{t('web:contains_all_tags')}</ToggleButton>
                            </ToggleButtonGroup>
                        </Box>
                    </Collapse>
                    <ThickDivider mt={0} />
                    <SelectItemWithoutOptions
                        title={t('web:advanced_filters')}
                        onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                        endIcon={
                            showAdvancedFilters ? (
                                <ExpandLessIcon sx={{ color: 'var(--text-secondary)' }} />
                            ) : (
                                <ExpandMoreIcon sx={{ color: 'var(--text-secondary)' }} />
                            )
                        }
                        showValue={false}
                    />
                    <Collapse in={showAdvancedFilters}>
                        {rangeFilterGroups.map((group, i) => (
                            <React.Fragment key={i}>
                                {i > 0 && <Divider className={styles.filterSubDivider} />}
                                {group.map(rangeSlider)}
                            </React.Fragment>
                        ))}
                    </Collapse>
                    <ThickDivider mt={0} />
                    <SimpleItemWithSwitch
                        id="se-travel-show-start-finish"
                        text={t('web:show_start_finish_icons')}
                        checked={ttx.travelShowStartFinish}
                        onChange={() => ttx.setTravelShowStartFinish((v) => !v)}
                    />
                </Box>
                <ColorBlock color={'#f0f0f0'} />
            </Box>
        </SecondaryMenuDrawer>
    );
}

// the menu scale plus a radius dragged on the map to a value off the scale
function radiusOptions(radius) {
    return SEARCH_RADIUS_OPTIONS.includes(radius)
        ? SEARCH_RADIUS_OPTIONS
        : [...SEARCH_RADIUS_OPTIONS, radius].sort((a, b) => a - b);
}
